import CustomChevronDown from "@bevco/MobiusOverrides/Icons/CustomChevronDown";
import CustomChevronUp from "@bevco/MobiusOverrides/Icons/CustomChevronUp";
import { colorStrings, mediaQueriesBP } from "@bevco/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconProps } from "@insite/mobius/Icon";    
import Img, { ImgProps } from "@insite/mobius/Img";
import { Button, GridItem } from "@insite/mobius/index";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    countries = "countries",
    customCSS = "customCSS",
    countryImage = "countryImage",
    altText = "altText",
    destination = "destination",
    title = "title",
}

export interface CountryModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        countryImage: string;
        altText: string;
        title: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.countries]: CountryModel[];
        [fields.customCSS]: string;
        [fields.countryImage]: string;
        [fields.altText]: string;
        [fields.title]: string;
    };
    extendedStyles?: CountrySelectorStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { session } = state.context;
    const currentUrl = `${state.context.website.uri.split("/api")[0]}${state.data.pages.location.pathname}`;
    
    return {
        currentLocation: getLocation(state),
        currentUrl,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CountrySelectorStyles {
    countrySelectorWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    img?: ImgProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    customSelectWrapper?: InjectableCss;
    selectedOption?: ButtonPresentationProps;
    optionsList?: InjectableCss;
    option?: InjectableCss;
    listTitle?: InjectableCss;
    optionImg?: ImgProps;
    linkOption?: InjectableCss;
    menuItemIcon?: IconProps;
}

export const countrySelectorStyles: CountrySelectorStyles = {
    countrySelectorWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },

    img: {
        css: css`
            display: block;
            max-width: 30px !important;
            height: auto !important;

            ${mediaQueriesBP.minMD} {
                max-width: 50px !important;
            }

            ${mediaQueriesBP.minLG} {
                max-width: 45px !important;
            }
        `,
    },
    optionImg: {
        css: css`
            max-width: 25px !important;
            height: auto !important;

            ${mediaQueriesBP.minMD} {
                max-width: 35px !important;
            }

            ${mediaQueriesBP.minLG} {
                max-width: 45px !important;
            }
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    customSelectWrapper: {
        css: css`
            position: relative;
            cursor: pointer;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;

            button:focus {
               outline: none !important;
            }
        `,
    },
    selectedOption: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0;
            gap: 1px;
            border: none;

            ${mediaQueriesBP.minLG} {
                gap: 10px;
                padding: 0;
                margin-right: 0;
                padding-left: 20px !important;
            }

            @media (max-width: 768px) {
                min-height: 25px;
                height: 25px;
            }
        `,
    },
    optionsList: {
        css: css`
            position: absolute;
            background-color: white;
            border: 1px solid #bbbbbb57;
            width: 165px;
            overflow: visible;
            z-index: ${getProp("theme.zIndex.toaster")};

            flex-direction: column;
            max-width: none !important;
            padding: 0;
            top: 100%;
            right: 0;

            &::before {
                content: "";
                position: absolute;
                top: -5px;
                right: 20px;
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-bottom: 5px solid ${getColor(colorStrings.commonGlobalWhite)};
                z-index: 9999;
            }
        `,
    },
    option: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px;
            gap: 25px;
            max-width: 100% !important;
            border-bottom: 1px solid #bbbbbb57;
            width: 100%;

            &:hover {
                background-color: ${getColor(colorStrings.commonBackground)};
            }
        `,
    },
    listTitle: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px;
            max-width: 100% !important;
            width: 100%;
            border-bottom: 1px solid #bbbbbb57;
            line-height: 10px;
        `,
    },
    linkOption: {
        css: css`
            width: 100%;
            span {
                line-height: 14px;
                color: ${getColor(colorStrings.textMain)};
                font-size: 14px;
                display: flex;
                align-items: center;
                gap: 15px;
            }
        `,
    },

    menuItemIcon: {
        css: css`
            display: inline-block;
            color: ${getColor(colorStrings.textMain)} !important;

            svg {
                fill: ${getColor(colorStrings.textMain)};
            }
        `,
        size: 18,
        color: colorStrings.textMain,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const CountrySelector: FC<Props> = ({ fields, extendedStyles, currentLocation,currentUrl }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedCountry, setSelectedCountry] = React.useState<CountryModel | undefined>(undefined);
    const containerRef = useRef<HTMLDivElement>(null);
    const countrySet = useRef(false);
    const countriesLinks = useGetLinks(fields.countries, o => o.fields.destination);
    const countries = fields.countries.map((country, index) => ({
        ...country,
        fields: {
            ...country.fields,
            url: countriesLinks.links[index]?.url,
        },
    }));

    if (!countries) {
        return null;
    }

    const styles = mergeToNew(countrySelectorStyles, extendedStyles);

    const icon = <StyledIcon src={isOpen ? CustomChevronUp : CustomChevronDown} {...styles.menuItemIcon} />;
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const fieldChangeHandler = ({ target: { value } }: { target: { value: string } }) => {
        const newSelected = countries.find(country => country.fields.title === value);
        setSelectedCountry(newSelected);
        setIsOpen(false);
    };

    useEffect(() => {
        if (countries.length > 0 && !countrySet.current) {
            setSelectedCountry(countries.find(country => country.fields.url?.includes("bevco")));
            countrySet.current = true;
        }
    }, [countries]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case "Enter":
            case " ":
                event.preventDefault();
                setIsOpen(prev => !prev);
                break;
            case "Escape":
                setIsOpen(false);
                break;
            case "ArrowDown":
                event.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (isOpen) {
                    setIsOpen(true);
                }
                break;
        }
    };

    const handleClickOutside = useCallback((event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    return (
        <StyledWrapper
            {...customCssWrapper}
            {...styles.customSelectWrapper}
            ref={containerRef}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls="country-list"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="list-wrapper"
        >
            <Button
                {...styles.selectedOption}
                onClick={() => setIsOpen(prev => !prev)}
                role="button"
                aria-label={selectedCountry ? selectedCountry.fields.title : "Select a country"}
                data-test-selector="country-selector-button"
                variant="tertiary"
            >
                {selectedCountry && (
                    <Img
                        src={selectedCountry.fields.countryImage}
                        altText={selectedCountry.fields.altText}
                        {...styles.img}
                    />
                )}
                <ButtonIcon src={isOpen ? CustomChevronUp : CustomChevronDown} {...styles.menuItemIcon} />
            </Button>

            {isOpen && (
                <GridItem {...styles.optionsList} role="listbox" id="country-list" aria-label="Select a country">
                    <GridItem {...styles.listTitle}>
                        <Typography>{translate("Website Location")}:</Typography>
                    </GridItem>
                    {countries.map((link, index) => (
                        <GridItem
                            {...styles.option}
                            key={index}
                            role="option"
                            aria-selected={selectedCountry?.fields.title === link.fields.title}
                            tabIndex={selectedCountry?.fields.title === link.fields.title ? 0 : -1}
                            onClick={() => {
                                fieldChangeHandler({ target: { value: link.fields.title } });
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    fieldChangeHandler({ target: { value: link.fields.title } });
                                }
                            }}
                        >
                            <Link {...styles.linkOption} href={link.fields.destination.value} target={link.fields.openInNewWindow ? "_blank" : ""}>
                                <Img
                                    src={link.fields.countryImage}
                                    altText={link.fields.altText}
                                    {...styles.optionImg}
                                />
                                <Typography {...styles.title}>{link.fields.title}</Typography>
                            </Link>
                        </GridItem>
                    ))}
                </GridItem>
            )}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CountrySelector),
    definition: {
        displayName: "Country Selector",
        group: "Common",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.countries,
                displayName: "Countries",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        title,
                    } = item.fields;
                    if (title) {
                        return title;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: fields.destination,
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                    {
                        name: fields.countryImage,
                        displayName: "Country Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: fields.altText,
                        displayName: "Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        tab: basicTab,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
